import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, model, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';

import { MdwButton } from '@spaces-ui/meadow/components/button';
import { Calendar_Fill } from '@spaces-ui/meadow/icons/svgs';

import { DateRangeValue } from '../../models';
import { MdwDatePickerHeader } from '../date-picker-header/date-picker-header';

interface DateRangeFormGroup {
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
}

@Component({
  selector: 'mdw-date-range-picker',
  templateUrl: './date-range-picker.html',
  styleUrls: ['./date-range-picker.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, MatDatepickerModule, MdwButton, ReactiveFormsModule],
})
export class MdwDateRangePicker {
  readonly header = MdwDatePickerHeader;

  public value = model<DateRangeValue | undefined>();
  public icon = input<string>(Calendar_Fill);
  public placeholder = input<string>('Choose a date range');
  public dateFormatOptions = input<Intl.DateTimeFormatOptions>({
    month: 'short',
    day: 'numeric',
  });
  public triggerSize = input<'small' | 'medium' | 'large' | 'x-large'>('medium');
  public triggerStyle = input<'muted' | 'secondary'>('secondary');
  public updateTriggerColor = input<boolean>(false);
  public allowClear = input<boolean>();

  public picker = viewChild(MatDateRangePicker);

  public formGroup?: FormGroup<DateRangeFormGroup>;

  public editingDateRange?: DateRangeValue;

  public triggerClasses = computed(() => {
    return {
      [`${this.triggerSize()}-button`]: true,
      [`mdw-${this.triggerStyle()}`]: true,
    };
  });

  public dateFilter = (d: Date | null): boolean => {
    if (!d) return false;

    // Get the start of today
    const todayStart = new Date(new Date().setHours(0, 0, 0, 0));

    // Return true if the date is today or earlier
    return d < new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  };

  public displayValue = computed(() => {
    const value = this.value();
    const placeholder = this.placeholder();

    if (!value) {
      return placeholder;
    }

    const formattedStartValue = new Intl.DateTimeFormat('en-US', this.dateFormatOptions()).format(
      value.startDate,
    );
    const formattedEndValue = new Intl.DateTimeFormat('en-US', this.dateFormatOptions()).format(
      value.endDate,
    );

    const formattedValue = `${formattedStartValue} - ${formattedEndValue}`;

    return formattedValue;
  });

  public isPlaceholder = computed(() => {
    return !this.value();
  });

  public constructor() {
    effect(() => {
      const value = this.value();

      if (value && !this.formGroup) {
        this.initForm(value);
      } else if (value === undefined) {
        this.clearValue();
      }
    });
  }

  public openPicker() {
    const openPicker = () => {
      const picker = this.picker();
      if (picker) {
        picker.open();
      }
    };
    if (!this.formGroup) {
      this.initForm(undefined);

      setTimeout(() => openPicker());
    } else {
      openPicker();
    }
  }

  public submitValue() {
    const formValue = this.formGroup?.value;

    if (!formValue || (!formValue.startDate && !formValue.endDate)) {
      return;
    }

    this.value.set({
      startDate: formValue.startDate ?? undefined,
      endDate: formValue.endDate ?? undefined,
    });
  }

  public clearValue() {
    const picker = this.picker();

    if (!picker) {
      return;
    }

    this.formGroup?.controls.startDate.setValue(null);
    this.formGroup?.controls.endDate.setValue(null);
    this.value.set(undefined);
    picker.close();
  }

  private initForm(initialValue?: DateRangeValue) {
    this.formGroup = new FormGroup<DateRangeFormGroup>({
      startDate: new FormControl(initialValue?.startDate ?? null),
      endDate: new FormControl(initialValue?.endDate ?? null),
    });
  }
}
